/**
 * Created by Ning Jiang on 3/22/2017.
 */

import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {GameMode} from "@nolimitcity/slot-game/bin/core/gamemode/GameMode";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {ScreenEvent} from "@nolimitcity/slot-game/bin/core/screen/event/ScreenEvent";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {
    IStillImageBackgroundConfig,
    StillImageBackground
} from "@nolimitcity/slot-game/bin/game/background/StillImageBackground";
import {TweenLite} from "gsap";
import {IWixxGameModeChangedData} from "../gamemode/WixxGameMode";
import {WixxWinPresentationEvent} from "../winpresentation/event/WixxWinPresentationEvent";
import {Wixx} from "../Wixx";
import {WixxGameConfig} from "../WixxGameConfig";

export class WixxBackground extends StillImageBackground {

    private static readonly FEATURE_IMAGES = {
        RED_BOOSTER : "background_red",
        YELLOW_BOOSTER : "background_yellow",
        BLUE_BOOSTER : "background_blue",
        RED_YELLOW_BOOSTER : "background_red",
        RED_BLUE_BOOSTER : "background_blue",
        YELLOW_BLUE_BOOSTER : "background_yellow"
    };

    private static readonly FEATURE_IMAGES_PORTRAIT_BORDER = {
        RED_BOOSTER : "background_portrait_border_red",
        YELLOW_BOOSTER : "background_portrait_border_yellow",
        BLUE_BOOSTER : "background_portrait_border_blue",
        RED_YELLOW_BOOSTER : "background_portrait_border_red",
        RED_BLUE_BOOSTER : "background_portrait_border_blue",
        YELLOW_BLUE_BOOSTER : "background_portrait_border_yellow"
    };

    private static readonly FEATURE_IMAGES_PORTRAIT_EXTENSIONS = {
        RED_BOOSTER : "background_portrait_ex_red",
        YELLOW_BOOSTER : "background_portrait_ex_yellow",
        BLUE_BOOSTER : "background_portrait_ex_blue",
        RED_YELLOW_BOOSTER : "background_portrait_ex_red",
        RED_BLUE_BOOSTER : "background_portrait_ex_blue",
        YELLOW_BLUE_BOOSTER : "background_portrait_ex_yellow"
    };

    private _gradientHeight:number;
    private _featureBackgroundSprite:PIXI.Sprite;
    private _topExtension:PIXI.Sprite;
    private _bottomExtension:PIXI.Sprite;
    private _topBorder:PIXI.Sprite;
    private _bottomBorder:PIXI.Sprite;
    private _topGradient:PIXI.Sprite;
    private _bottomGradient:PIXI.Sprite;
    private _currentFeature:string;
    private _portraitCoverLayer:PIXI.Container;
    private _particlesContainer:PIXI.Container;

    constructor(config:IStillImageBackgroundConfig) {
        super(config);
    }

    protected addEventListeners():void {
        super.addEventListeners();

        EventHandler.addEventListener(this, ScreenEvent.GAME_START, (event:GameEvent) => this.onParticles());
        EventHandler.addEventListener(this, WixxWinPresentationEvent.BIG_WIN_PRESENTATIONS_STOPPED, (event:GameEvent) => this.onParticles());
    }

    private onParticles():void {
        if(Wixx.particles == null) {
            return;
        }

        Wixx.particles.play(1, this._particlesContainer, this._layer);
    }

    protected initAnimations():void {
        super.initAnimations();

        this._featureBackgroundSprite = new PIXI.Sprite();
        this._featureBackgroundSprite.width = WixxGameConfig.instance.GAME_WIDTH;
        this._featureBackgroundSprite.height = WixxGameConfig.instance.GAME_HEIGHT;
        this._featureBackgroundSprite.visible = false;
        this.addChild(this._featureBackgroundSprite);

        this._particlesContainer = new PIXI.Container();
        this.addChild(this._particlesContainer);

        this.addPortraitBackgroundCover();
    }

    private addPortraitBackgroundCover() {
        const topY:number = GameConfig.instance.REEL_AREA_MASK_RECT!.y;
        const bottomY:number = GameConfig.instance.REEL_AREA_MASK_RECT!.bottom;
        this._portraitCoverLayer = StageManager.getLayer(WixxGameConfig.instance.LAYERS.PORTRAIT_SPECIFIC.name);

        this._topExtension = new PIXI.Sprite(GameResources.getTextures("background_portrait_ex")[0]);
        this._topExtension.x = 0;
        this._topExtension.y = topY;
        this._topExtension.anchor.set(0, 0);
        this._portraitCoverLayer.addChild(this._topExtension);

        this._bottomExtension = new PIXI.Sprite(GameResources.getTextures("background_portrait_ex_yellow")[0]);
        this._bottomExtension.x = 0;
        this._bottomExtension.y = bottomY;
        this._bottomExtension.anchor.set(0, 0);
        this._portraitCoverLayer.addChild(this._bottomExtension);

        this._topGradient = new PIXI.Sprite(GameResources.getTextures("top_gradient")[0]);
        this._topGradient.x = 0;
        this._topGradient.y = topY;
        this._topGradient.anchor.set(0, 0);
        this._gradientHeight = this._topGradient.height;
        this._portraitCoverLayer.addChild(this._topGradient);

        this._bottomGradient = new PIXI.Sprite(GameResources.getTextures("top_gradient")[0]);
        this._bottomGradient.x = 0;
        this._bottomGradient.y = bottomY;
        this._bottomGradient.anchor.set(0, 0);
        this._portraitCoverLayer.addChild(this._bottomGradient);

        this._topBorder = new PIXI.Sprite(GameResources.getTextures("background_portrait_border")[0]);
        this._topBorder.x = 0;
        this._topBorder.y = topY;
        this._topBorder.anchor.set(0, 0.5);
        // NOTE: don't know why the scale is 4 times bigger than it should be. guess it's something relevant to the smartloading 25%?
        this._topBorder.scale.x = GameConfig.instance.GAME_WIDTH * 0.25;
        this._topBorder.blendMode = PIXI.BLEND_MODES.ADD;
        this._portraitCoverLayer.addChild(this._topBorder);

        this._bottomBorder = new PIXI.Sprite(GameResources.getTextures("background_portrait_border_yellow")[0]);
        this._bottomBorder.x = 0;
        this._bottomBorder.y = bottomY;
        this._bottomBorder.anchor.set(0, 0.5);
        this._bottomBorder.scale.x = GameConfig.instance.GAME_WIDTH * 0.25;
        this._bottomBorder.blendMode = PIXI.BLEND_MODES.ADD;
        this._portraitCoverLayer.addChild(this._bottomBorder);
    }

    protected onChangeBackground(data:IWixxGameModeChangedData):boolean {
        super.onChangeBackground({newMode : GameMode.NORMAL});

        if(data.feature == null) {
            return false;
        }

        if(data.feature === this._currentFeature) {
            return false;
        }

        const duration:number = WixxGameConfig.instance.GAME_FEATURE_CHANGE_ANIMATION_DURATION;
        if(data.feature === "BASE") {
            TweenLite.to(this._featureBackgroundSprite, duration, {
                alpha : 0,
                onComplete : () => {
                    this._featureBackgroundSprite.visible = false;
                }
            });
            this._topExtension.texture = GameResources.getTextures("background_portrait_ex")[0];
            this._bottomExtension.texture = GameResources.getTextures("background_portrait_ex_yellow")[0];
            this._topBorder.texture = GameResources.getTextures("background_portrait_border")[0];
            this._bottomBorder.texture = GameResources.getTextures("background_portrait_border_yellow")[0];
        } else {
            this._featureBackgroundSprite.texture = GameResources.getTextures((<any>WixxBackground.FEATURE_IMAGES)[data.feature])[0];
            TweenLite.fromTo(this._featureBackgroundSprite, duration, {alpha : 0}, {alpha : 1});
            this._featureBackgroundSprite.visible = true;
            this._topExtension.texture = GameResources.getTextures((<any>WixxBackground.FEATURE_IMAGES_PORTRAIT_EXTENSIONS)[data.feature])[0];
            this._bottomExtension.texture = GameResources.getTextures((<any>WixxBackground.FEATURE_IMAGES_PORTRAIT_EXTENSIONS)[data.feature])[0];
            this._topBorder.texture = GameResources.getTextures((<any>WixxBackground.FEATURE_IMAGES_PORTRAIT_BORDER)[data.feature])[0];
            this._bottomBorder.texture = GameResources.getTextures((<any>WixxBackground.FEATURE_IMAGES_PORTRAIT_BORDER)[data.feature])[0];
        }
        this._currentFeature = data.feature;
        return true;
    }

    protected onResize(data:IResizeData):void {
        this._topGradient.scale.y = Math.min(-1, (0 - this._layer.y / this._layer.scale.y) / this._gradientHeight) * 0.25;
        this._topExtension.scale.y = (0 - this._layer.y / this._layer.scale.y) * 0.25;

        const bottomExtensionHeight:number = (data.height - this._portraitCoverLayer.y) / this._portraitCoverLayer.scale.y - GameConfig.instance.REEL_AREA_MASK_RECT!.bottom;
        this._bottomGradient.scale.y = bottomExtensionHeight / this._gradientHeight * 0.25;
        this._bottomExtension.scale.y = bottomExtensionHeight * 0.25;
    }
}